import * as React from 'react';

/**
 * Applies the platform's saved theme — colours, fonts and corner radius —
 * to the running console.
 *
 * The design tokens that carry the brand are CSS custom properties
 * (`--sk-brand`, `--sk-brand-accent`), so changing them here re-colours every
 * screen at once without a rebuild: a colour picked in Platform settings →
 * Appearance reaches every open browser within a minute.
 *
 * Read from the public endpoint because the sign-in page has to paint in the
 * right colour before anyone has signed in.
 */

export interface Appearance {
  primaryColor: string;
  accentColor: string;
  sidebarColor: string;
  fontBody: string;
  fontDisplay: string;
  cornerRadius: number;
}

/** `#4F46E5` → `243.4 75.4% 58.6%`, the bare triplet Tailwind composes alpha with. */
export function hexToHslTriplet(hex: string): string | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) return null;

  const int = Number.parseInt(match[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return `0 0% ${(l * 100).toFixed(1)}%`;

  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;

  return `${h.toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%`;
}

/** Loads a Google font once, by family name. */
function ensureFont(family: string): void {
  if (!family) return;
  const id = `sk-font-${family.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

export function applyAppearance(theme: Partial<Appearance>): void {
  const root = document.documentElement;

  const brand = theme.primaryColor ? hexToHslTriplet(theme.primaryColor) : null;
  if (brand) root.style.setProperty('--sk-brand', brand);

  const accent = theme.accentColor ? hexToHslTriplet(theme.accentColor) : null;
  if (accent) root.style.setProperty('--sk-brand-accent', accent);

  const chrome = theme.sidebarColor ? hexToHslTriplet(theme.sidebarColor) : null;
  if (chrome) root.style.setProperty('--sk-surface-chrome', chrome);

  if (typeof theme.cornerRadius === 'number') {
    root.style.setProperty('--radius', `${theme.cornerRadius / 16}rem`);
  }

  if (theme.fontBody) {
    ensureFont(theme.fontBody);
    root.style.setProperty('--sk-font-body', `'${theme.fontBody}'`);
  }
  if (theme.fontDisplay) {
    ensureFont(theme.fontDisplay);
    root.style.setProperty('--sk-font-display', `'${theme.fontDisplay}'`);
  }
}

/**
 * Fetches the theme once on mount and applies it. Deliberately not a
 * TanStack query: this runs before the providers are mounted on the sign-in
 * page, and a failure must never stop the console from rendering — it simply
 * keeps the palette compiled into the build.
 */
export function useAppearance(): void {
  React.useEffect(() => {
    let cancelled = false;
    const base = import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:4000';

    // JSON from the network is unknown until it is narrowed, which is the
    // rule everywhere else in this codebase too.
    const isAppearance = (value: unknown): value is Partial<Appearance> =>
      typeof value === 'object' && value !== null;

    fetch(`${base}/public/appearance`)
      .then((r): Promise<unknown> => (r.ok ? r.json() : Promise.resolve(null)))
      .then((body: unknown) => {
        if (cancelled || typeof body !== 'object' || body === null) return;
        const data = (body as { data?: unknown }).data;
        if (!isAppearance(data)) return;
        applyAppearance(data);
      })
      .catch(() => {
        /* The shipped palette stands. */
      });

    return () => {
      cancelled = true;
    };
  }, []);
}
